import {Fragment, type ReactNode} from 'react'
import {PorQue} from '@/components/ui/por-que'

/** El vacío lo resuelve el llamador con su palabra: la fila nunca desaparece. */
type FilaFicha = {
  clave: string
  etiqueta: string
  valor: ReactNode
  /** Solo se pinta si la ficha trae `marcador`. */
  marcada?: boolean
}

export type BloqueFicha = {
  clave: string
  /** ≤ 2 palabras. */
  titulo: string
  filas: FilaFicha[]
  /** Una vez por bloque, nunca por fila (U4/U5). */
  procedencia?: {label: string; contenido: ReactNode; testId?: string; contenidoTestId?: string}
  /** Qué significan los datos, una vez en el encabezado (U4). `ReactNode` porque `ui/` no importa de
   * `components/`. */
  ayuda?: ReactNode
  testId?: string
}

/** Las dos columnas se deciden por el ancho del bloque (`@container` + `@md:`), no de la ventana: en
 * una columna angosta la consulta de ventana partía el correo letra a letra. */
export function FichaDatos({
  bloques,
  marcador,
  testId,
}: {
  bloques: BloqueFicha[]
  /** Sin esto no se pinta punto ni leyenda (ficha congelada); el punto nunca va sin su palabra (U17). */
  marcador?: {leyenda: string}
  testId?: string
}) {
  return (
    <div className="space-y-2" data-testid={testId}>
      {bloques.map((bloque) => {
        const marcadas = Boolean(marcador) && bloque.filas.some((fila) => fila.marcada)
        return (
          <section
            key={bloque.clave}
            className="@container rounded-lg border border-border/60 px-3 py-2"
            data-testid={bloque.testId}>
            <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
              <h4 className="font-mono text-[10px] font-medium tracking-wider text-muted-foreground uppercase">
                {bloque.titulo}
              </h4>
              {bloque.ayuda}
              {marcadas && marcador && (
                <span className="inline-flex items-center gap-1 text-[10px] text-muted-foreground">
                  <Punto />
                  {marcador.leyenda}
                </span>
              )}
            </div>

            <dl className="mt-1.5 grid grid-cols-1 gap-x-3 gap-y-1 text-sm @md:grid-cols-[11.5rem_minmax(0,1fr)]">
              {bloque.filas.map((fila) => (
                <Fragment key={fila.clave}>
                  <dt className="text-muted-foreground">{fila.etiqueta}</dt>
                  <dd className="mb-1 min-w-0 break-words sm:mb-0">
                    {fila.valor}
                    {marcador && fila.marcada && (
                      <span className="ml-1.5 inline-flex items-center align-middle">
                        <Punto />
                        <span className="sr-only">{marcador.leyenda}</span>
                      </span>
                    )}
                  </dd>
                </Fragment>
              ))}
            </dl>

            {bloque.procedencia && (
              <div className="mt-1.5">
                <PorQue
                  label={bloque.procedencia.label}
                  // T11: el nombre accesible dice de qué bloque es la traza.
                  ariaLabel={`${bloque.procedencia.label} — ${bloque.titulo}`}
                  testId={bloque.procedencia.testId}
                  contenidoTestId={bloque.procedencia.contenidoTestId}
                  enFlujo>
                  {bloque.procedencia.contenido}
                </PorQue>
              </div>
            )}
          </section>
        )
      })}
    </div>
  )
}

/** Tono neutro, nunca ámbar: un dato marcado no está pendiente. */
function Punto() {
  return <span aria-hidden="true" className="inline-block h-1.5 w-1.5 shrink-0 rounded-full bg-muted-foreground/60" />
}
